<template>
    <div class="tutorial-fake-fight">
        <p class="tutorial-fake-fight-caption">{{ captionText }}</p>

        <div class="tutorial-fake-fight-battle">
            <BattleLayout :choose-mode="phase === 'choose'">
                <template #player>
                    <CombatantSlot
                        v-if="phase === 'choose'"
                        kind="choose"
                        min-height="100%"
                        @open-picker="pickerOpen = true"
                    />
                    <CombatantSlot
                        v-else
                        kind="pokemon"
                        fight
                        :pokemon="playerPokemonView"
                        :opponent-pokemon="magikarpView"
                        min-height="100%"
                        side="Left"
                    />
                </template>

                <template #opponent>
                    <CombatantSlot
                        kind="pokemon"
                        :pokemon="magikarpView"
                        :opponent-pokemon="phase === 'choose' ? null : playerPokemonView"
                        :show-details="phase !== 'choose'"
                        min-height="100%"
                        side="Right"
                    />
                </template>

                <template v-if="phase !== 'choose'" #center>
                    <div class="tutorial-fake-fight-report" :class="$vuetify.theme.dark ? 'transparent06--dark' : 'transparent06'" ref="reportCard">
                        <div class="tutorial-fake-fight-report-scale-wrap" ref="reportContent" :style="{transform: 'scale(' + reportScale + ')'}">
                            <template v-if="lastResult">
                                <div class="tutorial-fake-fight-report-banner">
                                    {{ $t('journey.tutorialFakeFight.lastRoundPrefix') }} {{ lastResult.side === 'player' ? $t('journey.tutorialFakeFight.yourAttack') : $t('journey.tutorialFakeFight.yourDefense') }}
                                </div>
                                <div class="tutorial-fake-fight-report-row">
                                    <div class="tutorial-fake-fight-report-side">
                                        <div class="tutorial-fake-fight-report-skill" :class="{'tutorial-fake-fight-report-skill--base': !lastResult.playerSkill}">
                                            <TypePill v-if="lastResult.playerSkill" :type="lastResult.playerSkill.type" size="small">{{ lastResult.playerSkill.name }}</TypePill>
                                            <span v-else>{{ $t('journey.tutorialFakeFight.baseMove') }}</span>
                                        </div>
                                        <div class="tutorial-fake-fight-report-value">🎲 {{ lastResult.playerRoll }}</div>
                                    </div>
                                    <div class="tutorial-fake-fight-report-vs">vs</div>
                                    <div class="tutorial-fake-fight-report-side">
                                        <div class="tutorial-fake-fight-report-skill tutorial-fake-fight-report-skill--base"><span>{{ $t('journey.tutorialFakeFight.baseMove') }}</span></div>
                                        <div class="tutorial-fake-fight-report-value">🎲 {{ lastResult.magikarpRoll }}</div>
                                    </div>
                                </div>
                                <div v-if="lastResult.dmg > 0" class="tutorial-fake-fight-report-primary">
                                    <div class="tutorial-fake-fight-report-primary-num">-{{ lastResult.dmg }}</div>
                                    <!-- cíl dmg je vždy Magikarp - hráč v tutorialu nikdy nedostane reálnou škodu (bezpečnostní garance BE) -->
                                    <div class="tutorial-fake-fight-report-primary-name">Magikarp</div>
                                    <div v-if="lastResult.typeMultiplier > 1" class="tutorial-fake-fight-report-eff tutorial-fake-fight-report-eff--super">{{ $t('journey.tutorialFakeFight.superEffective') }}</div>
                                    <div v-else-if="lastResult.typeMultiplier < 1" class="tutorial-fake-fight-report-eff tutorial-fake-fight-report-eff--weak">{{ $t('journey.tutorialFakeFight.notVeryEffective') }}</div>
                                </div>
                            </template>
                            <div v-else class="tutorial-fake-fight-report-banner">{{ $t('journey.tutorialFakeFight.battleStart') }}</div>
                        </div>
                    </div>
                </template>

                <template v-if="isFightingPhase" #skill-actions>
                    <SkillActionBar
                        :skills="currentRoundSkills"
                        :selected-skill-id="selectedSkillId"
                        :can-fight="!isResolving"
                        :base-label="$t('journey.tutorialFakeFight.baseMove')"
                        :round-role="phase === 'player-attack' ? 'attack' : 'defence'"
                        allow-base-move
                        require-selection
                        @open-picker="skillPickerOpen = true"
                        @clear-skill="selectedSkillId = null"
                        @use-skill="useSelectedSkill"
                        @use-base-move="useBaseMove"
                    />
                </template>

                <template #actions>
                    <v-card-actions v-if="phase === 'done'" class="justify-center transparent">
                        <v-btn
                            color="primary black--text btn-shadow"
                            @click="$emit('complete')"
                        >
                            {{ $t('journey.tutorialFakeFight.continueBtn') }}
                        </v-btn>
                    </v-card-actions>
                </template>
            </BattleLayout>
        </div>

        <PokemonPickerDialog
            v-model="pickerOpen"
            :pokemons="[pokemon]"
            @choose="onChoosePokemon"
        />
        <SkillPickerDialog
            v-model="skillPickerOpen"
            :skills="currentRoundSkills"
            @choose="(skill) => selectedSkillId = skill.id"
        />
        <ResponsiveDialog
            v-model="showUseSkillHint"
            :max-width="360"
            :full-height="false"
            :header-title="$t('journey.tutorialFakeFight.hintTitle')"
            header-class="skin-window-header--oak"
            card-class="skin-card"
        >
            <v-card-text class="tutorial-fake-fight-hint-text pa-4 text-center">
                {{ $t('journey.tutorialFakeFight.hintText', {
                    kind: phase === 'player-attack' ? $t('journey.tutorialFakeFight.hintAttackWord') : $t('journey.tutorialFakeFight.hintDefenseWord'),
                    baseMove: $t('journey.tutorialFakeFight.baseMove'),
                }) }}
            </v-card-text>
        </ResponsiveDialog>
    </div>
</template>

<script>
    // Naskriptovaný souboj s Magikarpem ze skutečných prezentačních kousků
    // reálné bojové obrazovky (BattleLayout/CombatantSlot/SkillActionBar/
    // SkillPickerDialog/PokemonPickerDialog) - viz claude/components/journey-oak-league.md.
    import BattleLayout from '@/components/journey/positions/battle/BattleLayout';
    import CombatantSlot from '@/components/journey/positions/battle/CombatantSlot';
    import SkillActionBar from '@/components/journey/positions/battle/SkillActionBar';
    import SkillPickerDialog from '@/components/journey/positions/battle/SkillPickerDialog';
    import PokemonPickerDialog from '@/components/journey/positions/battle/PokemonPickerDialog';
    import ResponsiveDialog from '@/components/utils/ResponsiveDialog';
    import TypePill from '@/components/utils/TypePill';
    import {mapActions} from 'vuex';
    import {getTutorialFightStep, setTutorialFightStep} from '@/utils/tutorialFightStorage';

    const MAGIKARP_TYPE = {id: 'WATER', name: 'Water'};
    const MAGIKARP_MAX_HP = 7;
    const MAGIKARP_BASE = {
        id: -1,
        name: 'Magikarp',
        level: 1,
        maxHp: MAGIKARP_MAX_HP,
        pokemonType: {
            name: 'Magikarp',
            img: '/129.png',
            type: MAGIKARP_TYPE,
            skills: [],
        },
    };
    // jen první útok hráče je garantovaně omezený, ať Magikarpa nezabije rovnou - dál jede reálný náhodný výsledek
    const FIRST_HIT_TARGET_DMG = 2;

    export default {
        name: 'TutorialFakeFight',
        components: {BattleLayout, CombatantSlot, SkillActionBar, SkillPickerDialog, PokemonPickerDialog, ResponsiveDialog, TypePill},
        props: {
            pokemon: {type: Object, required: true},
            trainerId: {type: Number, required: true},
        },
        data() {
            const stored = getTutorialFightStep(this.trainerId);
            const hasSnapshot = stored && typeof stored === 'object';
            return {
                phase: hasSnapshot ? stored.turn + '-attack' : 'choose',
                magikarpHp: hasSnapshot ? stored.magikarpHp : MAGIKARP_MAX_HP,
                playerHp: hasSnapshot ? stored.playerHp : this.pokemon.hp,
                usedSkillIds: hasSnapshot ? stored.usedSkillIds : [],
                // targetDmg smí ovlivnit jen doopravdy první úder hráče, ne každé jeho "player-attack" kolo
                firstHitResolved: hasSnapshot ? !!stored.firstHitResolved : false,
                lastResult: null,
                pickerOpen: false,
                skillPickerOpen: false,
                selectedSkillId: null,
                showUseSkillHint: false,
                isResolving: false,
                // dynamické zmenšení reportu, stejný recept jako skutečná FightReport.vue (updateReportScale)
                reportScale: 1,
                reportResizeObserver: null,
            }
        },
        computed: {
            isFightingPhase() {
                return this.phase === 'player-attack' || this.phase === 'magikarp-attack';
            },
            magikarpView() {
                return {...MAGIKARP_BASE, hp: Math.max(0, this.magikarpHp)};
            },
            playerPokemonView() {
                return {...this.pokemon, hp: Math.max(0, this.playerHp)};
            },
            availableRealSkills() {
                if (!this.isFightingPhase) {
                    return [];
                }
                const wantDefence = this.phase === 'magikarp-attack';
                return this.pokemon.pokemonType.skills.filter((s) => {
                    if (this.pokemon.skillIds.indexOf(s.id) === -1) {
                        return false;
                    }
                    return wantDefence ? s.defence > 0 : s.defence === 0;
                });
            },
            unusedRealSkill() {
                return this.availableRealSkills.find((s) => this.usedSkillIds.indexOf(s.id) === -1) || null;
            },
            currentRoundSkills() {
                return this.availableRealSkills.map((s) => ({
                    ...s,
                    disabled: this.usedSkillIds.indexOf(s.id) !== -1,
                    formula: s.attackFormula + (s.dmgFormula ? ' / ' + s.dmgFormula : ''),
                }));
            },
            captionText() {
                if (this.phase === 'choose') {
                    return this.$t('journey.tutorialFakeFight.captionChoose');
                }
                const baseMove = this.$t('journey.tutorialFakeFight.baseMove');
                if (this.phase === 'player-attack') {
                    return this.unusedRealSkill
                        ? this.$t('journey.tutorialFakeFight.captionPlayerAttackHasSkill', {baseMove})
                        : this.$t('journey.tutorialFakeFight.captionPlayerAttackNoSkill', {baseMove});
                }
                if (this.phase === 'magikarp-attack') {
                    return this.unusedRealSkill
                        ? this.$t('journey.tutorialFakeFight.captionMagikarpAttackHasSkill')
                        : this.$t('journey.tutorialFakeFight.captionMagikarpAttackNoSkill', {baseMove});
                }
                return this.$t('journey.tutorialFakeFight.captionDone');
            },
        },
        watch: {
            // phase navíc řeší první vykreslení reportu - #center neexistuje v 'choose' fázi, refy nejsou k dispozici dřív
            phase() {
                this.$nextTick(() => {
                    this.attachReportResizeObserver();
                    this.updateReportScale();
                });
            },
            lastResult() {
                this.$nextTick(this.updateReportScale);
            },
        },
        mounted() {
            this.reportResizeObserver = new ResizeObserver(() => this.updateReportScale());
            this.$nextTick(() => {
                this.attachReportResizeObserver();
                this.updateReportScale();
            });
        },
        beforeDestroy() {
            if (this.reportResizeObserver) {
                this.reportResizeObserver.disconnect();
            }
        },
        methods: {
            ...mapActions({
                tutorialFightRound: 'journey/tutorialFightRound',
            }),
            // stejný dynamicky-zmenšující trik jako skutečná FightReport.vue - #center má pevnou výšku a tvrdé overflow:hidden
            attachReportResizeObserver() {
                if (this.$refs.reportCard && this.reportResizeObserver) {
                    this.reportResizeObserver.observe(this.$refs.reportCard);
                }
            },
            updateReportScale() {
                const card = this.$refs.reportCard;
                const content = this.$refs.reportContent;
                if (!card || !content) {
                    return;
                }
                const availableHeight = card.clientHeight;
                const contentHeight = content.scrollHeight;
                if (!availableHeight || !contentHeight) {
                    return;
                }
                this.reportScale = Math.min(1, availableHeight / contentHeight);
            },
            onChoosePokemon() {
                this.phase = 'player-attack';
            },
            useSelectedSkill() {
                this.resolveRound(this.selectedSkillId);
            },
            useBaseMove() {
                if (this.unusedRealSkill) {
                    this.showUseSkillHint = true;
                    return;
                }
                this.resolveRound(null);
            },
            resolveRound(skillId) {
                if (this.isResolving) {
                    return;
                }
                this.isResolving = true;
                const attacker = this.phase === 'player-attack' ? 'player' : 'magikarp';
                const targetDmg = (attacker === 'player' && !this.firstHitResolved) ? FIRST_HIT_TARGET_DMG : null;
                this.tutorialFightRound({attacker, skillId, targetDmg}).then((result) => {
                    this.isResolving = false;
                    if (!result) {
                        return;
                    }
                    const usedSkill = skillId !== null
                        ? this.pokemon.pokemonType.skills.find((s) => s.id === skillId) || null
                        : null;
                    if (skillId !== null) {
                        this.usedSkillIds.push(skillId);
                    }
                    this.selectedSkillId = null;
                    // úspěšná hráčova obrana proti útoku Magikarpa vrací škodu přes result.counterDmg, ne result.dmg
                    const dmgToMagikarp = attacker === 'player' ? result.dmg : (result.counterDmg || 0);
                    this.lastResult = {
                        side: attacker,
                        // skillId patří vždy hráči (Magikarp žádný reálný skill nemá) - stejný tvar jako FightReportDuel.vue
                        playerSkill: usedSkill,
                        playerRoll: attacker === 'player' ? result.attackerRoll : result.defenderRoll,
                        magikarpRoll: attacker === 'player' ? result.defenderRoll : result.attackerRoll,
                        dmg: dmgToMagikarp,
                        typeMultiplier: result.typeMultiplier,
                    };
                    if (attacker === 'player') {
                        this.firstHitResolved = true;
                    } else {
                        this.playerHp = Math.max(0, this.playerHp - result.dmg);
                    }
                    this.magikarpHp = Math.max(0, this.magikarpHp - dmgToMagikarp);
                    if (this.magikarpHp <= 0) {
                        this.phase = 'done';
                        setTutorialFightStep(this.trainerId, 'complete');
                        return;
                    }
                    this.phase = attacker === 'player' ? 'magikarp-attack' : 'player-attack';
                    setTutorialFightStep(this.trainerId, {
                        turn: this.phase === 'player-attack' ? 'player' : 'magikarp',
                        magikarpHp: this.magikarpHp,
                        playerHp: this.playerHp,
                        usedSkillIds: this.usedSkillIds,
                        firstHitResolved: this.firstHitResolved,
                    });
                });
            },
        },
    }
</script>

<style scoped>
.tutorial-fake-fight {
    height: 100%;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}
.tutorial-fake-fight-caption {
    flex: none;
    text-align: center;
    font-size: 14px;
    /* padding na všechny strany - rodičovský .oak-tutorial-next-content během souboje nemá vlastní padding */
    padding: 10px 16px;
    margin-bottom: 4px;
}
.tutorial-fake-fight-battle {
    flex: 1 1 auto;
    min-height: 0;
    position: relative;
}
/* report kopíruje strukturu skutečné FightReportDuel.vue - .tutorial-fake-fight-report je pevná "klipovací"
   plocha, .tutorial-fake-fight-report-scale-wrap uvnitř je position:absolute a dynamicky se zmenšuje
   (viz reportScale ve scriptu), ať se obsah vejde bez oříznutí do omezené výšky #center slotu */
.tutorial-fake-fight-report {
    position: relative;
    height: 100%;
    margin: 4px 10px;
    border-radius: 8px;
    font-size: 12px;
    overflow: hidden;
}
.tutorial-fake-fight-report-scale-wrap {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    transform-origin: top center;
    text-align: center;
    padding: 6px 10px;
}
.tutorial-fake-fight-report-banner {
    border-radius: 6px;
    padding: 3px 0;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.03em;
    margin-bottom: 4px;
    background: linear-gradient(90deg, #8a6d1f, #b8901f);
    color: #fff;
}
.tutorial-fake-fight-report-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 4px;
}
.tutorial-fake-fight-report-side {
    flex: 1;
    min-width: 0;
}
.tutorial-fake-fight-report-skill {
    font-size: 12px;
    font-weight: 700;
    margin-top: 4px;
    /* dlouhý název skillu by se bez tohohle mohl zalomit na 2 řádky a vytlačit zbytek reportu mimo pevný #center slot */
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.tutorial-fake-fight-report-skill--base span {
    display: inline-block;
    border-radius: 8px;
    padding: 1px 8px;
    font-size: 10px;
    font-weight: 400;
    background: rgba(255, 255, 255, 0.12);
}
.tutorial-fake-fight-report-value {
    font-family: monospace;
    font-weight: 800;
    border-radius: 5px;
    padding: 0 8px;
    margin-top: 2px;
    font-size: 13px;
    display: inline-block;
    background: rgba(255, 255, 255, 0.1);
}
.tutorial-fake-fight-report-vs {
    font-size: 10px;
    opacity: 0.5;
    padding-top: 12px;
}
.tutorial-fake-fight-report-primary {
    margin-top: 3px;
}
.tutorial-fake-fight-report-primary-num {
    font-size: 20px;
    font-weight: 900;
    line-height: 1;
    color: #ff5252;
}
.tutorial-fake-fight-report-primary-name {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.03em;
}
.tutorial-fake-fight-report-eff {
    display: inline-block;
    margin-top: 2px;
    padding: 1px 8px;
    border-radius: 10px;
    font-size: 9px;
    font-weight: 800;
    color: #fff;
}
.tutorial-fake-fight-report-eff--super {
    background: #b71c1c;
}
.tutorial-fake-fight-report-eff--weak {
    background: #1b5e20;
}
/* rezerva pro plovoucí zavírací křížek (ResponsiveDialog), stejných 69px jako ostatní next/ popupy */
.tutorial-fake-fight-hint-text {
    padding-bottom: var(--responsive-dialog-close-clearance) !important;
}
@media (min-width: 960px) {
    .tutorial-fake-fight-hint-text {
        padding-bottom: 16px !important;
    }
}
</style>
